import { useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  Settings,
  CreditCard,
  Video,
  Mail,
  MessageSquare,
  HardDrive,
  Shield,
  ChevronRight
} from 'lucide-react';

interface SettingCard {
  title: string;
  description: string;
  icon: any;
  color: string;
  bgColor: string;
  href: string;
}

export default function SettingsOverviewPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (profile && profile.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [profile, navigate]);

  const settingCards: SettingCard[] = [
    {
      title: 'Configurazione Pagamenti',
      description: 'Configura PayPal, Stripe e bonifico bancario',
      icon: CreditCard,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      href: '/dashboard/settings/payments',
    },
    {
      title: 'Configurazione Twilio',
      description: 'Imposta le videochiamate e le live session',
      icon: Video,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      href: '/dashboard/settings/twilio',
    },
    {
      title: 'Configurazione Email',
      description: 'Configura SMTP e servizi email',
      icon: Mail,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      href: '/dashboard/settings/email',
    },
    {
      title: 'Configurazione WhatsApp',
      description: 'Integra notifiche WhatsApp Business',
      icon: MessageSquare,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      href: '/dashboard/settings/whatsapp',
    },
    {
      title: 'Configurazione Storage',
      description: 'Gestisci spazio web per video e foto',
      icon: HardDrive,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      href: '/dashboard/settings/storage',
    },
  ];

  if (profile?.role !== 'admin') {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Impostazioni Applicazione</h1>
                <p className="text-gray-600 mt-1">Configura i servizi e le integrazioni della piattaforma</p>
              </div>
            </div>
          </div>
          <Badge variant="secondary" className="text-sm">
            <Shield className="w-4 h-4 mr-1" />
            Solo Admin
          </Badge>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {settingCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card
                key={card.href}
                className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 border-2 hover:border-blue-300"
                onClick={() => navigate(card.href)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-3 ${card.bgColor} rounded-lg`}>
                        <Icon className={`w-6 h-6 ${card.color}`} />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{card.title}</CardTitle>
                        <CardDescription className="mt-2">
                          {card.description}
                        </CardDescription>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Nota sulla sicurezza</h3>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Le chiavi API sono memorizzate in modo sicuro nel database</li>
                  <li>Solo gli amministratori possono visualizzare e modificare queste impostazioni</li>
                  <li>Non condividere mai le tue chiavi API con nessuno</li>
                  <li>Usa le modalita di test prima di passare alla produzione</li>
                </ul>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    </DashboardLayout>
  );
}
